import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Mail, Phone, Search, SlidersHorizontal, Users } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;
const transferBanks = ['BCA', 'BNI', 'BRI', 'MANDIRI', 'PERMATA'];

const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function formatBookingDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRupiah(value) {
  return `Rp ${Math.round(Number(value || 0)).toLocaleString('id-ID')}`;
}

function getInitials(name) {
  return String(name || 'Customer')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'C';
}

export default function ClassBookings() {
  const [bookings, setBookings] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSchedule, setSelectedSchedule] = useState('all');
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState('');

  const [selectedClassKey, setSelectedClassKey] = useState('');
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [transferBank, setTransferBank] = useState('BCA');
  const [xenditCheckout, setXenditCheckout] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const [bookingsRes, schedulesRes] = await Promise.all([
        axios.get(`${API}/class-bookings`, { withCredentials: true }),
        axios.get(`${API}/schedules`, { withCredentials: true }),
      ]);
      setBookings(Array.isArray(bookingsRes.data) ? bookingsRes.data : []);
      setSchedules(Array.isArray(schedulesRes.data) ? schedulesRes.data : []);
    } catch (error) {
      console.error('Failed to fetch class bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const scheduleOptions = useMemo(
    () =>
      [...schedules].sort((a, b) => {
        const dayDiff = dayOrder.indexOf(a.day_of_week) - dayOrder.indexOf(b.day_of_week);
        if (dayDiff !== 0) return dayDiff;
        return String(a.start_time || '').localeCompare(String(b.start_time || ''));
      }),
    [schedules]
  );

  const filteredBookings = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return bookings.filter((booking) => {
      const matchesSchedule = selectedSchedule === 'all' || booking.schedule_id === selectedSchedule;
      const searchable = [
        booking.full_name,
        booking.email,
        booking.phone,
        booking.schedule_name,
        booking.day_of_week,
        booking.start_time,
        booking.status,
        booking.payment_status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return matchesSchedule && (!query || searchable.includes(query));
    });
  }, [bookings, searchQuery, selectedSchedule]);

  const groupedClasses = useMemo(() => {
    const scheduleMap = new Map(
      schedules.map((schedule) => [String(schedule.id), schedule])
    );
    const groups = new Map();
    for (const booking of filteredBookings) {
      const key = String(booking.schedule_id || booking.schedule_name || booking.id);
      const schedule = scheduleMap.get(String(booking.schedule_id || ''));
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          schedule_id: booking.schedule_id,
          schedule_name: booking.schedule_name || '-',
          day_of_week: booking.day_of_week || '-',
          start_time: booking.start_time || '-',
          end_time: booking.end_time || '-',
          capacity: Number(schedule?.capacity || 0),
          enrolled: Number(schedule?.enrolled || 0),
          bookings: [],
        });
      }
      groups.get(key).bookings.push(booking);
    }
    return Array.from(groups.values()).sort((a, b) => a.schedule_name.localeCompare(b.schedule_name));
  }, [filteredBookings, schedules]);

  const selectedClass = useMemo(
    () => groupedClasses.find((item) => item.key === selectedClassKey) || null,
    [groupedClasses, selectedClassKey]
  );

  const selectedClassSummary = useMemo(() => {
    if (!selectedClass) {
      return { total: 0, paid: 0, pending: 0 };
    }
    const total = selectedClass.bookings.length;
    const paid = selectedClass.bookings.filter(
      (booking) => String(booking.payment_status || '').toLowerCase() === 'paid'
    ).length;
    return { total, paid, pending: total - paid };
  }, [selectedClass]);

  const totalToday = bookings.filter((booking) => {
    const created = new Date(booking.created_at);
    if (Number.isNaN(created.getTime())) return false;
    const now = new Date();
    return (
      created.getFullYear() === now.getFullYear() &&
      created.getMonth() === now.getMonth() &&
      created.getDate() === now.getDate()
    );
  }).length;

  const totalSchedulesWithBookings = groupedClasses.length;

  const handleConfirmBooking = async (bookingId) => {
    try {
      setProcessingId(bookingId);
      await axios.patch(`${API}/class-bookings/${bookingId}/confirm`, { status: 'confirmed' }, { withCredentials: true });
      await fetchBookings();
    } catch (error) {
      console.error('Failed to confirm booking:', error);
      alert(error.response?.data?.detail || 'Gagal konfirmasi booking.');
    } finally {
      setProcessingId('');
    }
  };

  const openPaymentModal = (booking) => {
    setSelectedBooking(booking);
    setPaymentAmount(String(Number(booking.amount || 50000)));
    setPaymentMethod('Cash');
    setTransferBank('BCA');
    setXenditCheckout(null);
    setIsPaymentModalOpen(true);
  };

  const handleCreatePayment = async () => {
    if (!selectedBooking?.id) return;
    const amount = Number(paymentAmount || 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      alert('Nominal pembayaran tidak valid.');
      return;
    }

    try {
      setProcessingId(selectedBooking.id);
      if (paymentMethod === 'Cash') {
        await axios.post(
          `${API}/class-bookings/${selectedBooking.id}/pay`,
          { amount, payment_method: paymentMethod },
          { withCredentials: true }
        );
        setIsPaymentModalOpen(false);
        await fetchBookings();
        return;
      }

      const { data } = await axios.post(
        `${API}/class-bookings/${selectedBooking.id}/xendit`,
        {
          amount,
          payment_method: paymentMethod,
          transfer_bank: transferBank,
        },
        { withCredentials: true }
      );
      setXenditCheckout(data);
    } catch (error) {
      console.error('Failed to process class booking payment:', error);
      alert(error.response?.data?.detail || 'Gagal memproses pembayaran booking.');
    } finally {
      setProcessingId('');
    }
  };

  const checkXenditPaymentStatus = async () => {
    if (!selectedBooking?.id || !xenditCheckout?.checkout_id) return;
    try {
      setCheckingStatus(true);
      const { data } = await axios.get(
        `${API}/class-bookings/${selectedBooking.id}/xendit/${xenditCheckout.checkout_id}`,
        { withCredentials: true }
      );
      setXenditCheckout((prev) => ({ ...(prev || {}), ...data }));
      if (data?.is_paid) {
        alert('Pembayaran berhasil diterima.');
        setIsPaymentModalOpen(false);
        await fetchBookings();
      }
    } catch (error) {
      console.error('Failed to check class booking payment status:', error);
      alert(error.response?.data?.detail || 'Gagal cek status pembayaran.');
    } finally {
      setCheckingStatus(false);
    }
  };

  const copyVirtualAccount = async () => {
    const va = xenditCheckout?.virtual_account_number;
    if (!va) return;
    try {
      await navigator.clipboard.writeText(va);
      alert('Nomor VA berhasil disalin.');
    } catch (_) {
      alert('Gagal menyalin nomor VA.');
    }
  };

  return (
    <DashboardLayout>
      <Header title="Class Bookings" />
      <div className="p-8" data-testid="class-bookings-page">
        <div className="mb-6 overflow-hidden border border-white/10 bg-[linear-gradient(135deg,rgba(212,175,55,0.08),rgba(9,9,11,0.65)_42%,rgba(255,255,255,0.025))]">
          <div className="flex flex-col gap-6 p-6 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <Badge className="mb-3 rounded-sm border border-[#D4AF37]/25 bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/10">
                Booking Class
              </Badge>
              <h2 className="text-2xl font-semibold text-white">Daftar Class Booking</h2>
              <p className="mt-2 text-sm leading-6 text-zinc-500">
                Tampilkan nama class lebih ringkas. Klik class untuk melihat daftar nama yang sudah booking.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-3 lg:min-w-[460px]">
              <div className="border border-white/10 bg-black/20 px-4 py-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Total Booking</p>
                <p className="mt-2 text-2xl font-semibold text-white">{bookings.length}</p>
              </div>
              <div className="border border-white/10 bg-black/20 px-4 py-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Hari Ini</p>
                <p className="mt-2 text-2xl font-semibold text-[#D4AF37]">{totalToday}</p>
              </div>
              <div className="border border-white/10 bg-black/20 px-4 py-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Total Class</p>
                <p className="mt-2 text-2xl font-semibold text-white">{totalSchedulesWithBookings}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-5 grid gap-3 border border-white/10 bg-zinc-950/35 p-3 lg:grid-cols-[1fr_280px]">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-600" strokeWidth={1.6} />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cari nama, email, nomor HP, atau kelas..."
              className="h-11 rounded-sm border-white/10 bg-black/20 pl-11 text-white placeholder:text-zinc-600 focus:border-[#D4AF37]"
              data-testid="class-bookings-search"
            />
          </div>
          <div className="relative">
            <SlidersHorizontal className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-600" strokeWidth={1.6} />
            <select
              value={selectedSchedule}
              onChange={(e) => setSelectedSchedule(e.target.value)}
              className="h-11 w-full rounded-sm border border-white/10 bg-black/20 px-4 pl-11 text-sm text-white outline-none focus:border-[#D4AF37] [&_option]:bg-white [&_option]:text-black"
              data-testid="class-bookings-filter"
            >
              <option value="all">Semua kelas</option>
              {scheduleOptions.map((schedule) => (
                <option key={schedule.id} value={schedule.id}>
                  {schedule.name} - {schedule.day_of_week} {schedule.start_time}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-[360px_1fr]">
          <div className="overflow-hidden border border-white/10 bg-zinc-950/35">
            <div className="border-b border-white/10 bg-white/[0.025] px-4 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
              Nama Class
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-14">
                <div className="h-7 w-7 animate-spin rounded-full border-2 border-[#D4AF37] border-t-transparent" />
              </div>
            ) : groupedClasses.length > 0 ? (
              <div className="divide-y divide-white/5">
                {groupedClasses.map((group) => (
                  <button
                    key={group.key}
                    type="button"
                    onClick={() => setSelectedClassKey(group.key)}
                    className={`w-full px-4 py-3 text-left transition-colors ${
                      selectedClassKey === group.key ? 'bg-[#D4AF37]/10' : 'hover:bg-white/[0.03]'
                    }`}
                  >
                    <p className="text-sm font-semibold text-white">{group.schedule_name}</p>
                    <p className="mt-1 text-xs text-zinc-500">
                      {group.day_of_week} • {group.start_time} - {group.end_time}
                    </p>
                    <p className="mt-1 text-xs text-[#F3D77A]">
                      Booking/Slot: {group.enrolled || group.bookings.length}/{group.capacity || group.enrolled || group.bookings.length}
                    </p>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-5 text-sm text-zinc-500">Belum ada class booking.</div>
            )}
          </div>

          <div className="overflow-hidden border border-white/10 bg-zinc-950/35 shadow-[0_20px_80px_rgba(0,0,0,0.22)]">
            <div className="border-b border-white/10 bg-white/[0.025] px-5 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
              {selectedClass ? `Daftar Booking: ${selectedClass.schedule_name}` : 'Pilih class untuk lihat daftar booking'}
            </div>

            {selectedClass ? (
              <div>
                <div className="grid gap-3 border-b border-white/10 bg-black/20 px-5 py-3 sm:grid-cols-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Total Booking</p>
                    <p className="mt-1 text-xl font-semibold text-white">{selectedClassSummary.total}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Sudah Paid</p>
                    <p className="mt-1 text-xl font-semibold text-[#D4AF37]">{selectedClassSummary.paid}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Pending</p>
                    <p className="mt-1 text-xl font-semibold text-zinc-300">{selectedClassSummary.pending}</p>
                  </div>
                </div>
                <div className="max-h-[520px] divide-y divide-white/5 overflow-y-auto">
                {selectedClass.bookings.map((booking, index) => (
                  (() => {
                    const bookingStatus = String(booking.status || '').toLowerCase();
                    const paymentStatus = String(booking.payment_status || '').toLowerCase();
                    const isFullyDone = bookingStatus === 'confirmed' && paymentStatus === 'paid';
                    return (
                  <div
                    key={booking.id}
                    className="grid gap-4 px-5 py-4 transition-colors hover:bg-white/[0.03] lg:grid-cols-[1.2fr_1fr_.8fr_1fr] lg:gap-5"
                    data-testid={`class-booking-row-${index}`}
                  >
                    <div className="flex min-w-0 items-start gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm border border-[#D4AF37]/20 bg-[#D4AF37]/10 text-sm font-bold text-[#D4AF37]">
                        {getInitials(booking.full_name)}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-white">{booking.full_name || 'Tanpa nama'}</p>
                        <p className="mt-1 text-xs text-zinc-500">Booking {formatBookingDate(booking.created_at)}</p>
                      </div>
                    </div>

                    <div className="min-w-0 space-y-2 text-xs text-zinc-500">
                      <p className="flex min-w-0 items-center gap-2">
                        <Mail className="h-3.5 w-3.5 shrink-0 text-zinc-600" strokeWidth={1.7} />
                        <span className="truncate">{booking.email || '-'}</span>
                      </p>
                      <p className="flex min-w-0 items-center gap-2">
                        <Phone className="h-3.5 w-3.5 shrink-0 text-zinc-600" strokeWidth={1.7} />
                        <span className="truncate">{booking.phone || '-'}</span>
                      </p>
                    </div>

                    <div className="flex flex-col items-start gap-2">
                      <span className="rounded-sm border border-emerald-400/20 bg-emerald-500/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-300">
                        {booking.status || 'booked'}
                      </span>
                      <span className={`rounded-sm border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] ${
                        String(booking.payment_status || 'Pending').toLowerCase() === 'paid'
                          ? 'border-[#D4AF37]/35 bg-[#D4AF37]/12 text-[#F3D77A]'
                          : 'border-white/15 bg-white/[0.03] text-zinc-400'
                      }`}>
                        {booking.payment_status || 'Pending'}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-start justify-end gap-2">
                      {!isFullyDone ? (
                        <>
                          {bookingStatus !== 'confirmed' ? (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => handleConfirmBooking(booking.id)}
                              disabled={processingId === booking.id}
                              className="h-8 rounded-sm border-white/15 bg-white/[0.02] px-3 text-xs text-white hover:bg-white/[0.06]"
                            >
                              Konfirmasi
                            </Button>
                          ) : null}
                          {bookingStatus === 'confirmed' ? (
                            <Button
                              type="button"
                              onClick={() => openPaymentModal(booking)}
                              className="h-8 rounded-sm bg-[#D4AF37] px-3 text-xs font-semibold text-black hover:bg-[#C59A27]"
                            >
                              Pembayaran
                            </Button>
                          ) : null}
                        </>
                      ) : (
                        <span className="rounded-sm border border-emerald-400/20 bg-emerald-500/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-300">
                          Selesai
                        </span>
                      )}
                    </div>
                  </div>
                    );
                  })()
                ))}
              </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <Users className="mb-3 h-10 w-10 text-zinc-700" strokeWidth={1.5} />
                <p className="text-sm font-medium text-white">Pilih class di sebelah kiri</p>
                <p className="mt-1 text-sm text-zinc-500">Setelah dipilih, daftar nama booking akan muncul di sini.</p>
              </div>
            )}
          </div>
        </div>

        <Dialog open={isPaymentModalOpen} onOpenChange={setIsPaymentModalOpen}>
          <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle>Pembayaran Booking Class</DialogTitle>
            </DialogHeader>
            {selectedBooking ? (
              <div className="mt-2 space-y-4">
                <div className="rounded-sm border border-white/10 bg-black/20 p-3">
                  <p className="text-sm font-semibold text-white">{selectedBooking.full_name}</p>
                  <p className="mt-1 text-xs text-zinc-500">{selectedBooking.schedule_name} • {selectedBooking.day_of_week} {selectedBooking.start_time}</p>
                </div>

                {!xenditCheckout ? (
                  <>
                    <div className="space-y-2">
                      <Label>Nominal (Rp)</Label>
                      <Input
                        type="number"
                        min={1}
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        className="bg-zinc-900 border-white/10 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Metode Pembayaran</Label>
                      <select
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="h-9 w-full rounded-sm border border-white/10 bg-zinc-900 px-3 text-sm text-white outline-none [&_option]:bg-white [&_option]:text-black"
                      >
                        <option value="Cash">Cash</option>
                        <option value="QRIS">QRIS (Xendit)</option>
                        <option value="Transfer">Transfer VA (Xendit)</option>
                      </select>
                    </div>
                    {paymentMethod === 'Transfer' && (
                      <div className="space-y-2">
                        <Label>Bank Transfer</Label>
                        <select
                          value={transferBank}
                          onChange={(e) => setTransferBank(e.target.value)}
                          className="h-9 w-full rounded-sm border border-white/10 bg-zinc-900 px-3 text-sm text-white outline-none [&_option]:bg-white [&_option]:text-black"
                        >
                          {transferBanks.map((bank) => <option key={bank} value={bank}>{bank}</option>)}
                        </select>
                      </div>
                    )}
                    <Button
                      type="button"
                      onClick={handleCreatePayment}
                      disabled={processingId === selectedBooking.id}
                      className="w-full bg-[#D4AF37] text-black hover:bg-[#C59A27]"
                    >
                      {processingId === selectedBooking.id ? 'Memproses...' : 'Proses Pembayaran'}
                    </Button>
                  </>
                ) : (
                  <div className="space-y-3">
                    <div className="text-center">
                      <p className="text-xs uppercase tracking-[0.16em] text-zinc-500">{xenditCheckout.payment_method}</p>
                      <p className="mt-1 text-xl font-semibold text-white">{formatRupiah(xenditCheckout.amount)}</p>
                      <p className="mt-1 text-xs text-zinc-400">Status: <span className="text-[#F3D77A]">{xenditCheckout.status || 'PENDING'}</span></p>
                    </div>
                    {xenditCheckout.payment_method === 'Transfer' ? (
                      <div className="rounded-sm border border-white/10 bg-white/[0.03] p-3">
                        <p className="text-xs text-zinc-500">{xenditCheckout.transfer_bank || 'Bank'} Virtual Account</p>
                        <p className="mt-1 break-all text-lg font-semibold text-white">{xenditCheckout.virtual_account_number || '-'}</p>
                        <Button type="button" variant="outline" onClick={copyVirtualAccount} className="mt-2 h-8 rounded-sm border-white/15 bg-white/[0.02] text-xs text-white">Copy VA</Button>
                      </div>
                    ) : (
                      <div className="mx-auto w-fit rounded-sm bg-white p-3">
                        {xenditCheckout.qr_string ? (
                          <img
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(xenditCheckout.qr_string)}`}
                            alt="QRIS Class Booking"
                            className="h-40 w-40"
                          />
                        ) : (
                          <p className="text-sm text-black">QR belum tersedia.</p>
                        )}
                      </div>
                    )}
                    {(xenditCheckout.qr_web_url || xenditCheckout.payment_web_url) && (
                      <a
                        href={xenditCheckout.qr_web_url || xenditCheckout.payment_web_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex h-9 w-full items-center justify-center rounded-sm border border-white/10 bg-white/[0.03] text-sm text-white hover:bg-white/[0.07]"
                      >
                        Buka Halaman Pembayaran
                      </a>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <Button type="button" onClick={checkXenditPaymentStatus} disabled={checkingStatus} className="bg-[#D4AF37] text-black hover:bg-[#C59A27]">
                        {checkingStatus ? 'Checking...' : 'Cek Status'}
                      </Button>
                      <Button type="button" variant="outline" onClick={() => setXenditCheckout(null)} className="border-white/15 bg-white/[0.02] text-white hover:bg-white/[0.07]">
                        Kembali
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
